import { Check, CheckCircle } from "@mui/icons-material";
import { Box, Button, DialogActions, DialogContent, Divider, FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import convertCurrency, { convertIntMoney } from "src/helper/convertCurrency";
import ChangeAddressDialog from "./ChangeAddressDialog";
import { TransitionGroup } from "react-transition-group";
import { orderStatusFormatted, orderTrackingType, TRANSPORTATION_TYPE, userRoleType } from "src/constant";
import { FormProvider, useForm } from "react-hook-form";
import FormControlAutocomplete from "src/components/shared/FormControlAutocomplete";
import useOrderDetail from "src/hooks/orders/useOrderDetail";
import useCustomAddressDetail from "src/hooks/user/customer/useCustomAddressDetail";
import useAuthGlobal from "src/context/useAuthGlobal";
import useDebounce from "src/hooks/useDebounce";
import useInfiniteAutocomplete from "src/hooks/useInfiniteAutocomplete";
import FormControlInput from "src/components/shared/FormControlInput";
import ProductForm from "./OrderProductForm";
import { enqueueSnackbar } from "notistack";
import useOrderCreate from "src/hooks/orders/useOrderCreate";
import useAppGlobal from "src/context/useAppGlobal";
import useOrderUpdate from "src/hooks/orders/useOrderUpdate";
import usePaymentMethodList from "src/hooks/payment-method/usePaymentMethodList";
import useTPList from "src/hooks/setting/transportation-provider/useTPList";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import FormControlNumericInput from "src/components/shared/FormControlNumericInput";

const initialOrder = {
  qty: undefined,
  discount: undefined,
  productModel: null,
  prod: null,
};

const schema = yup.object().shape({
  customer: yup.object().shape({
    value: yup.string().required("Vui lòng chọn khách hàng"),
  }),
  warehouse: yup.object().shape({
    value: yup.string().required("Vui lòng chọn kho"),
  }),
  paymentMethod: yup.object().shape({
    value: yup.string().required("Vui lòng chọn hình thức thanh toán"),
  }),
  shippingFee: yup.number(),
  cod: yup.number().required("Vui lòng nhập phí COD").typeError("Vui lòng nhập phí COD").min(0, "Phí COD phải không âm"),
  note: yup.string().nullable().max(500, "Không nhập quá 500 ký tự"),
  pickupDate: yup.string().required("Vui lòng chọn ngày lấy hàng"),
  pickUpType: yup.string().required("Vui lòng chọn phương thức lấy hàng"),
  orderDetails: yup
    .array()
    .of(
      yup.object().shape({
        qty: yup
          .number()
          .required("Vui lòng nhập số lượng")
          .typeError("Vui lòng nhập số lượng")
          .min(1, "Số lượng phải lớn hơn 0")
          .test("qty-less-than-stock", "Số lượng phải nhỏ hơn tồn kho khả dụng", function checkQtyLessThanStock(value) {
            const stockQty = Number(this.parent?.productModel?.qty);
            if (!stockQty || !value) return true;
            return Number(value) <= stockQty;
          }),
        discount: yup
          .number()
          .required("Vui lòng nhập chiết khấu")
          .typeError("Vui lòng nhập chiết khấu")
          .min(0, "Chiết khấu phải không âm")
          .max(100, "Chiết khấu phải nhỏ hơn 100"),
        productModel: yup
          .object()
          .shape({
            value: yup.string().required("Vui lòng chọn dung tích").typeError("Vui lòng chọn dung tích"),
          })
          .required("Vui lòng chọn dung tích"),
        prod: yup
          .object()
          .shape({
            value: yup.string().required("Vui lòng chọn sản phẩm").typeError("Vui lòng chọn sản phẩm"),
          })
          .required("Vui lòng chọn sản phẩm"),
      }),
    )
    .required("Vui lòng chọn sản phẩm"),
});

export default function OrderCreateDialog({ item, whouseId, isUpdate = false }) {
  const { data: orderItem } = useOrderDetail(item?._id, {
    warehouse: whouseId,
    include: "Inventory",
  });
  const { userMe } = useAuthGlobal();
  const { closeDialog } = useAppGlobal();
  const [isChangedAddress, setIsChangedAddress] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState("");
  const [searchWarehouse, setSearchWarehouse] = useState("");
  const [productSearchInput, setProductSearchInput] = useState("");
  const debouncedSearchCustomer = useDebounce(searchCustomer, 500);
  const debouncedSearchWarehouse = useDebounce(searchWarehouse, 500);
  const debouncedProductSearch = useDebounce(productSearchInput, 500);
  const { data: paymentMethods } = usePaymentMethodList({ limit: 200, skip: 0 });
  const { data: shippingProviders } = useTPList({ limit: 200, skip: 0 });
  const { trigger: createOrder } = useOrderCreate();
  const { trigger: updateOrder } = useOrderUpdate(item?._id);

  const form = useForm({
    resolver: yupResolver(schema),
    values: {
      ...orderItem?.data,
      hasCOD: Number(orderItem?.data?.cod) > 0 ? "COD" : "noCOD",
      orderDetails: orderItem?.data?.orderDetails?.map((v) => ({
        ...v,
        prod: {
          label: v?.productModel?.product?.name,
          value: v?.productModel?.product?._id,
          brand: v?.productModel?.product?.brand,
        },
        productModel: {
          label: v?.productModel?.name,
          value: v?.productModel?._id,
          price: v?.productModel?.price,
          qty: v?.inventory?.productModel?.qty,
        },
        discount: v?.discount ?? 0,
      })),
      customer: {
        label: orderItem?.data?.customer?.fullName,
        value: orderItem?.data?.customer?._id,
      },
      warehouse: {
        label: orderItem?.data?.warehouse?.name,
        value: orderItem?.data?.warehouse?._id,
      },
      shippingProvider: {
        label: orderItem?.data?.shippingProvider?.name,
        value: orderItem?.data?.shippingProvider?._id,
      },
      paymentMethod: {
        label: orderItem?.data?.paymentMethod?.name,
        value: orderItem?.data?.paymentMethod?._id,
      },
    },
  });

  const { customer: customerForm, orderDetails, pickupDate, pickUpType, shippingFee, cod, hasCOD, warehouse: warehouseForm } = form.watch();
  const { data: customerAddress } = useCustomAddressDetail(customerForm?.value);

  const isAdminRole = userMe?.role?.name === userRoleType.admin;
  const effectiveWarehouseId = warehouseForm?.value || userMe?.currentWarehouse?._id || whouseId;

  const {
    items: customerItems,
    isLoading: customerLoading,
    loadingMore: customerLoadingMore,
    hasMore: customerHasMore,
    onLoadMore: onLoadMoreCustomer,
  } = useInfiniteAutocomplete(
    "/api/v1/customers",
    { limit: 10, searchField: "fullName", searchValue: debouncedSearchCustomer || undefined },
    !orderItem?.data?.customer,
    debouncedSearchCustomer,
  );

  const {
    items: warehouseItems,
    isLoading: warehouseLoading,
    loadingMore: warehouseLoadingMore,
    hasMore: warehouseHasMore,
    onLoadMore: onLoadMoreWarehouse,
  } = useInfiniteAutocomplete(
    "/api/v1/warehouses",
    { limit: 10, searchField: "name", searchValue: debouncedSearchWarehouse || undefined },
    isAdminRole && !orderItem?.data?.warehouse,
    debouncedSearchWarehouse,
  );

  const inventoryParams = useMemo(() => {
    const base = { warehouse: effectiveWarehouseId };
    const keyword = debouncedProductSearch?.trim();
    if (keyword) {
      return { ...base, searchField: "product", searchValue: keyword };
    }
    return base;
  }, [effectiveWarehouseId, debouncedProductSearch]);

  const {
    items: inventoryItems,
    isLoading: inventoryLoading,
    loadingMore: inventoryLoadingMore,
    hasMore: inventoryHasMore,
    onLoadMore: onLoadMoreInventory,
  } = useInfiniteAutocomplete(
    effectiveWarehouseId ? "/api/v1/inventories" : "",
    inventoryParams,
    Boolean(effectiveWarehouseId),
    `${effectiveWarehouseId}-${debouncedProductSearch}`,
  );

  const inventoryOptions = useMemo(
    () =>
      inventoryItems.map((v) => ({
        label: v?.product?.name,
        value: v?._id,
        brand: v?.product?.brand,
      })),
    [inventoryItems],
  );

  const handleInputChangeCustomer = (_, value, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchCustomer("");
      return;
    }
    setSearchCustomer(value);
  };

  const handleInputChangeWarehouse = (_, value, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchWarehouse("");
      return;
    }
    setSearchWarehouse(value);
  };

  useEffect(() => {
    setProductSearchInput("");
  }, [effectiveWarehouseId]);

  const handleChangeAddress = () => {
    setIsChangedAddress((prev) => !prev);
  };

  const handleCreateOrder = async (data) => {
    const recipient = customerAddress?.data?.find((v) => v?.isDefault === true);
    let res;
    const body = {
      shippingFee: Number(data?.shippingFee),
      cod: data?.hasCOD === "COD" ? Number(data?.cod) : 0,
      note: data?.note,
      trackingStatus: orderStatusFormatted.WaitToExport,
      pickUpType: data?.pickUpType,
      pickupDate: data?.pickupDate,
      paymentMethod: data?.paymentMethod?.value,
      shippingProvider: data?.shippingProvider?.value,
      orderDetails: data?.orderDetails?.map((v) => ({
        qty: Number(v?.qty),
        discount: Number(v?.discount),
        productModel: v?.productModel?.value,
      })),
    };
    if (!item) {
      res = await createOrder({
        body: {
          type: orderTrackingType.new,
          status: orderStatusFormatted.WaitToExport,
          pickUpAddress: [data?.warehouse?.detailAddress, data?.warehouse?.ward?.path].filter(Boolean).join(", "),
          warehouse: data?.warehouse?.value ?? whouseId,
          customer: data?.customer?.value,
          sales: data?.customer?.sales?._id,
          recipient: {
            detailAddress: [recipient?.detailAddress, recipient?.ward?.path].filter(Boolean).join(", "),
            name: data?.customer?.fullName,
            customerAddress: recipient?._id,
            phone: recipient?.phone,
            ward: recipient?.ward?._id,
          },
          ...body,
        },
      });
    } else {
      res = await updateOrder({
        body: {
          status: data?.status,
          pickUpAddress: data?.pickUpAddress,
          warehouse: data?.warehouse?._id ?? whouseId,
          recipient: {
            detailAddress: recipient?.detailAddress,
            name: data?.recipient?.name,
            phone: data?.recipient?.phone,
            ward: data?.recipient?.ward?._id,
            customerAddress: data?.recipient?.recipient?.customerAddress,
          },
          ...body,
        },
      });
    }

    if (res?.success) {
      closeDialog();
      form.reset();
      return enqueueSnackbar("Thành công", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message ?? "Lỗi khi tạo đơn hàng", { variant: "error" });
    }
  };

  const totalCOD = useMemo(() => {
    const result = orderDetails?.reduce((acc, curr) => {
      const price = curr?.productModel?.price || 0;
      const qty = curr?.qty || 0;
      const discount = Number(curr?.discount) || 0;
      const total = price * qty * (1 - discount / 100);
      return acc + total;
    }, 0);

    return Math.floor(result / 1000) * 1000;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState]);

  const customerOptions = useMemo(() => {
    return customerItems.map((v) => ({
      ...v,
      label: v?.fullName,
      value: v?._id,
    }));
  }, [customerItems]);

  const warehouseOptions = useMemo(() => {
    return warehouseItems.map((v) => ({
      ...v,
      label: v?.name,
      value: v?._id,
    }));
  }, [warehouseItems]);

  const datePickUp = useMemo(() => {
    return Array.from({ length: 3 }, (_, index) => {
      const newDate = !!orderItem?.data ? new Date(orderItem?.data?.createdAt) : new Date();
      newDate.setDate(newDate.getDate() + index);
      return newDate;
    });
  }, [orderItem?.data]);

  const shippingProviderOptions = useMemo(() => {
    return shippingProviders?.data?.map((v) => ({
      label: v?.name,
      value: v?._id,
    }));
  }, [shippingProviders]);

  const paymentMethodsOptions = useMemo(() => {
    return paymentMethods?.data?.map((v) => ({
      label: v?.name,
      value: v?._id,
    }));
  }, [paymentMethods]);

  useEffect(() => {
    const defaultCod = orderDetails?.reduce((acc, curr) => {
      const price = curr?.productModel?.price || 0;
      const qty = curr?.qty || 0;
      const discount = Number(curr?.discount) || 0;
      const total = price * qty * (1 - discount / 100) + Number(shippingFee);
      return acc + total;
    }, 0);
    form.setValue("cod", Math.floor(defaultCod / 1000) * 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails, shippingFee]);

  useEffect(() => {
    if (pickupDate || !datePickUp?.length) return;
    form.setValue("pickupDate", datePickUp[0].toISOString());
  }, [pickupDate, datePickUp, form]);

  return (
    <FormProvider {...form}>
      <DialogContent>
        {!isChangedAddress ? (
          <Stack gap="12px">
            <Box mt="12px">
              <FormControlAutocomplete
                name="customer"
                label="Khách hàng"
                options={customerOptions}
                placeholder="Khách hàng"
                disabled={!!orderItem?.data?.customer}
                required
                loading={customerLoading}
                hasMore={customerHasMore}
                loadingMore={customerLoadingMore}
                onLoadMore={onLoadMoreCustomer}
                filterOptions={(opts) => opts}
                onInputChange={handleInputChangeCustomer}
              />
            </Box>
            <Stack
              direction="row"
              gap="12px"
            >
              <Box
                sx={{
                  background: "var(--light-grey)",
                  width: "50%",
                  borderRadius: "6px",
                  p: "12px",
                }}
              >
                <Typography fontWeight="bold">NV phụ trách</Typography>
                <Typography variant="body2">Tên: {customerForm?.sales?.fullName ?? orderItem?.data?.customer?.fullName}</Typography>
                <Typography variant="body2">Email: {customerForm?.sales?.email ?? orderItem?.data?.customer?.email}</Typography>
                <Typography variant="body2">SDT: {customerForm?.sales?.phone ?? orderItem?.data?.customer?.phone}</Typography>
              </Box>
              {/* ADDRESS */}
              {customerAddress?.data?.map((v) => {
                return (
                  v?.isDefault && (
                    <Box
                      key={v?._id}
                      sx={{
                        background: "var(--light-grey)",
                        flex: 1,
                        borderRadius: "6px",
                        p: "12px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                        >
                          Địa chỉ cụ thể
                        </Typography>
                        <Button
                          onClick={handleChangeAddress}
                          variant="outlined"
                          sx={{
                            height: "22px",
                            fontSize: "12px",
                            p: 0,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Thay đổi
                        </Button>
                      </Box>
                      <Typography variant="body2">{v?.phone ?? "N/A"}</Typography>
                      <Typography variant="body2">{v?.detailAddress ?? "N/A"}</Typography>
                      <Typography variant="body2">{v?.ward?.path ?? "N/A"}</Typography>
                    </Box>
                  )
                );
              })}

              {(!customerAddress?.data?.length || !customerAddress?.data?.find((v) => v?.isDefault === true)) && (
                <Box
                  sx={{
                    width: "50%",
                    background: "var(--light-grey)",
                    borderRadius: "6px",
                    p: "12px",
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                  >
                    Địa chỉ cụ thể
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", mt: "6px" }}
                  >
                    Chưa thiết lập địa chỉ
                  </Typography>
                  <Button
                    onClick={() => setIsChangedAddress(!isChangedAddress)}
                    size="small"
                    sx={{ mt: "6px" }}
                  >
                    Thiết lập địa chỉ
                  </Button>
                </Box>
              )}
            </Stack>
            {isAdminRole && (
              <FormControlAutocomplete
                name="warehouse"
                label="Kho"
                options={warehouseOptions}
                placeholder="Kho"
                disabled={!!orderItem?.data?.warehouse}
                required
                loading={warehouseLoading}
                hasMore={warehouseHasMore}
                loadingMore={warehouseLoadingMore}
                onLoadMore={onLoadMoreWarehouse}
                filterOptions={(opts) => opts}
                onInputChange={handleInputChangeWarehouse}
              />
            )}
            <Divider sx={{ borderColor: "var(--grey) !important" }} />
            {/* BRAND LIST */}
            {orderDetails?.map((v, i) => {
              return (
                <ProductForm
                  key={i}
                  index={i}
                  disabled={isUpdate}
                  inventoryOptions={inventoryOptions}
                  inventoryData={inventoryItems}
                  onProductSearchChange={setProductSearchInput}
                  inventoryLoading={inventoryLoading}
                  inventoryLoadingMore={inventoryLoadingMore}
                  inventoryHasMore={inventoryHasMore}
                  onLoadMoreInventory={onLoadMoreInventory}
                />
              );
            })}
            {!isUpdate && (
              <Button
                variant="outlined"
                sx={{ width: "fit-content", alignSelf: "flex-end" }}
                onClick={() => form.setValue("orderDetails", [...orderDetails, initialOrder])}
              >
                Thêm sản phẩm
              </Button>
            )}

            {!!totalCOD && (
              <Box
                sx={{
                  borderTop: "1px solid var(--grey)",
                  gap: "12px",
                  mt: "12px",
                  p: "12px",
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  // background: "var(--grey)",
                }}
              >
                <Box textAlign="right">
                  <Typography sx={{ fontSize: "16px" }}>Tổng giá trị:</Typography>
                  <Typography sx={{ fontSize: "12px" }}>Chưa bao gồm phí ship</Typography>
                </Box>

                <Box gap="12px">
                  <Typography fontWeight="bold">{!!totalCOD ? convertIntMoney(totalCOD) : 0} đồng</Typography>
                  <Typography
                    fontWeight="bold"
                    sx={{ fontStyle: "italic", fontSize: "12px" }}
                  >
                    ({!!totalCOD ? convertCurrency(totalCOD) : 0})
                  </Typography>
                </Box>
              </Box>
            )}

            {/*-------- ADD DELIVERY */}
            <Box>
              <Typography
                fontWeight="bold"
                mb="12px"
              >
                Chọn ngày lấy hàng
              </Typography>
              <Box
                display="flex"
                gap="12px"
              >
                {datePickUp?.map((v, i) => {
                  return (
                    <Button
                      endIcon={pickupDate?.includes(v?.toISOString()?.slice(0, 10)) && <CheckCircle color="var(--success-txt)" />}
                      onClick={() => form.setValue("pickupDate", v?.toISOString())}
                      sx={{
                        borderRadius: "100px",
                        color: pickupDate?.includes(v?.toISOString()?.slice(0, 10)) ? "var(--success-txt) !important" : "var(--black)",
                        borderColor: pickupDate?.includes(v?.toISOString()?.slice(0, 10)) ? "var(--success-txt) !important" : "var(--black)",
                      }}
                      variant="outlined"
                      key={i}
                    >
                      {dayjs(v).format("dddd") + " - " + dayjs(v).format("DD-MM-YYYY")}
                    </Button>
                  );
                })}
              </Box>
            </Box>
            <Stack
              direction="row"
              gap="12px"
            >
              <Box
                onClick={() => form.setValue("pickUpType", TRANSPORTATION_TYPE.SELFDELIVERY)}
                sx={{
                  background: pickUpType === TRANSPORTATION_TYPE.SELFDELIVERY ? "var(--success)" : "var(--light-grey)",
                  opacity: 0.8,

                  borderRadius: "6px",
                  flex: 1,
                  p: "12px",
                  cursor: "pointer",
                }}
              >
                {pickUpType === TRANSPORTATION_TYPE.SELFDELIVERY && (
                  <Check
                    sx={{
                      fontSize: "18px",
                      display: "flex",
                      m: "0 0 -18px auto",
                      color: "var(--success-txt)",
                    }}
                  />
                )}
                <Typography
                  fontWeight="bold"
                  sx={{
                    color: pickUpType === TRANSPORTATION_TYPE.SELFDELIVERY ? "var(--success-txt)" : "var(--black)",
                  }}
                >
                  Gửi hàng tại bưu cục
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: pickUpType === TRANSPORTATION_TYPE.SELFDELIVERY ? "var(--success-txt)" : "var(--black)",
                  }}
                >
                  Bạn có thể gửi ở bất kì bưu cục:{" "}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: pickUpType === TRANSPORTATION_TYPE.SELFDELIVERY ? "var(--success-txt)" : "var(--black)",
                  }}
                >
                  Bưu cục gần nhất
                </Typography>
              </Box>
              <Box
                onClick={() => form.setValue("pickUpType", TRANSPORTATION_TYPE.PICKUP)}
                sx={{
                  background: pickUpType === TRANSPORTATION_TYPE.PICKUP ? "var(--success)" : "var(--light-grey)",
                  borderRadius: "6px",
                  opacity: 0.8,
                  flex: 1,
                  p: "12px",
                  cursor: "pointer",
                }}
              >
                {pickUpType === TRANSPORTATION_TYPE.PICKUP && (
                  <Check
                    sx={{
                      fontSize: "18px",
                      display: "flex",
                      m: "0 0 -18px auto",
                      color: "var(--success-txt)",
                    }}
                  />
                )}
                <Typography
                  fontWeight="bold"
                  sx={{
                    color: pickUpType === TRANSPORTATION_TYPE.PICKUP ? "var(--success-txt)" : "var(--black)",
                  }}
                >
                  Lấy hàng
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: pickUpType === TRANSPORTATION_TYPE.PICKUP ? "var(--success-txt)" : "var(--black)",
                  }}
                >
                  ĐVVC sẽ đến địa chỉ của bạn để lấy hàng
                </Typography>
                {!!warehouseForm?.detailAddress && (
                  <Typography
                    variant="body2"
                    mt="12px"
                    fontWeight="bold"
                    sx={{
                      color: pickUpType === TRANSPORTATION_TYPE.PICKUP ? "var(--success-txt)" : "var(--black)",
                    }}
                  >
                    {warehouseForm?.detailAddress + ", " + warehouseForm?.ward?.path}
                  </Typography>
                )}
              </Box>
            </Stack>
            <Box mt="12px">
              <FormControlAutocomplete
                name="shippingProvider"
                label="Đơn vị vận chuyển"
                options={shippingProviderOptions}
                placeholder="Đơn vị vận chuyển"
                disabled={!!orderItem?.data?.shippingProvider}
              />
            </Box>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12 / 2}
              >
                <FormControlAutocomplete
                  name="paymentMethod"
                  label="Hình thức thanh toán"
                  options={paymentMethodsOptions}
                  placeholder="Hình thức thanh toán"
                  required
                />
              </Grid>

              <Grid
                item
                xs={12 / 2}
              >
                <FormControlNumericInput
                  name="shippingFee"
                  label="Phí ship"
                  prefix="đ"
                  thousandSeparator
                  required
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", fontStyle: "italic" }}
                >
                  ({!!shippingFee ? convertCurrency(shippingFee) : 0} đồng)
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
              >
                <RadioGroup
                  row
                  sx={{ mb: 0.5 }}
                >
                  <FormControlLabel
                    value="COD"
                    checked={hasCOD === "COD"}
                    onChange={(e) => form.setValue("hasCOD", e.target.value)}
                    control={<Radio />}
                    label="COD"
                  />
                  <FormControlLabel
                    value="noCOD"
                    checked={hasCOD === "noCOD"}
                    onChange={(e) => form.setValue("hasCOD", e.target.value)}
                    control={<Radio />}
                    label="Không COD"
                  />
                </RadioGroup>
                {hasCOD === "COD" && (
                  <>
                    <FormControlNumericInput
                      name="cod"
                      label="COD"
                      prefix="đ"
                      thousandSeparator
                      required
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", fontStyle: "italic" }}
                    >
                      ({!!cod ? convertCurrency(cod) : 0} đồng)
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>

            <FormControlInput
              name="note"
              label="Ghi chú"
              placeholder="Ghi chú..."
              disabled={!!orderItem?.data?.note}
              multiline
              rows={4}
            />
          </Stack>
        ) : (
          <TransitionGroup>
            <ChangeAddressDialog
              handleChangeAddress={handleChangeAddress}
              customer={orderItem?.data?.customer}
            />
          </TransitionGroup>
        )}
      </DialogContent>
      {!isChangedAddress && (
        <DialogActions sx={{ p: "0 24px 24px" }}>
          <Button
            fullWidth
            variant="contained"
            onClick={form.handleSubmit(handleCreateOrder)}
          >
            Xác nhận
          </Button>
        </DialogActions>
      )}
    </FormProvider>
  );
}
